import { plainToInstance } from 'class-transformer'

import { GET_BOOKS_TITLE_AND_ID_BY_USER } from '@client/collections/MyWriting/schemas/getBooksTitleAndIdByUser'
import { GET_WRITER_SALES_ANALYTICS_DETAIL } from '@client/collections/MyWriting/schemas/getWriterSalesAnalyticsDetail'
import { GET_WRITER_SALES_ANALYTICS_OVERVIEW } from '@client/collections/MyWriting/schemas/getWriterSalesAnalyticsOverview'
import { GET_WRITING_LIST } from '@client/collections/MyWriting/schemas/getWritingList'
import { GET_WRITING_OPTIONS } from '@client/collections/MyWriting/schemas/getWritingOptions'
import {
  REMOVE_BOOK,
  REMOVE_BOOKS,
} from '@client/collections/MyWriting/schemas/removeBook'
import { UPDATE_BOOK_ISENDED } from '@client/collections/MyWriting/schemas/updateBookIsEnded'
import { UPDATE_BOOKS_STATUS } from '@client/collections/MyWriting/schemas/updateBookStatus'
import { gqlApiInstance } from '@client/init'
import { SortTableType } from '@features/myWriting/pages/MyWritingPage/components/SaleAnalyticsOverview/components/OverviewTabs/interface'
import { SearchFormType } from '@features/myWriting/pages/MyWritingPage/components/SearchForm/interface'
import { MyWritingOptionResponse } from '@models/myWriting/MyWritingOptionResponse'
import { MyWritingResponse } from '@models/myWriting/MyWritingResponse'
import { BookOptionType } from '@models/myWriting/salesAndAnalytics/BookOptionType'
import { WriterSalesAnalyticsDetailDataType } from '@models/myWriting/salesAndAnalytics/WriterSalesAnalyticsDetailDataType'
import { WriterSalesAnalyticsOverviewDataType } from '@models/myWriting/salesAndAnalytics/WriterSalesAnalyticsOverviewDataType'
import { WriterSalesAnalyticsDetailReportEnum } from '@interfaces/WriterSalesAnalyticsDetailReportEnum'
import { GET_MY_EBOOK } from '@client/collections/MyWriting/schemas/getMyEbook'
import { MyEbookResponse } from '@models/myWriting/MyEbookResponse'
import { SaleAndAnalyticsQueryParams } from '@features/myWriting/pages/MyWritingPage/components/SaleAnalyticsTab/interfaces/SaleAndAnalyticsQueryParams'
import { GET_WRITER_SALES_ANALYTICS_EBOOK_OVERVIEW } from '@client/collections/MyWriting/schemas/getWriterSalesAnalyticsEbookOverview'
import { WriterSalesAnalyticsOverviewEbookDataType } from '@models/myWriting/salesAndAnalytics/WriterSalesAnalyticsOverviewEbookDataType'
import { GET_EBOOKS_BY_USER } from '@client/collections/MyWriting/schemas/getEbooksTitleAndIdByUser'
import { EbookByUserIdType } from '@models/myWriting/salesAndAnalytics/EbookByUserIdType'
import { GET_WRITER_SALES_ANALYTICS_EBOOK_DETAIL } from '@client/collections/MyWriting/schemas/getWriterSalesAnalyticsEbookDetail'
import { SortEbookTableType } from '@features/myWriting/pages/MyWritingPage/components/EbookSaleAnalyticsOverview/components/EbookOverviewTabs/interface'
import { SalesAnalyticsDetailEbookDataType } from '@models/myWriting/salesAndAnalytics/SalesAnalyticsDetailEbookDataType'

export function useMyWritingAction() {
  async function getMyWriting(
    page: number,
    limitPerPage: number,
    searchParam: SearchFormType
  ): Promise<MyWritingResponse> {
    const { getWritingList } = await gqlApiInstance.request(GET_WRITING_LIST, {
      bookType: searchParam.bookType?.length ? searchParam.bookType : null,
      category: searchParam.category?.length
        ? searchParam.category.map(i => Number(i))
        : null,
      name: searchParam.param || null,
      penNameId: searchParam.penName?.length
        ? searchParam.penName.map(i => Number(i))
        : null,
      page,
      limitPerPage,
      orderBy: {
        createdAt: 'DESC',
      },
    })

    return plainToInstance(MyWritingResponse, getWritingList)
  }

  async function updateBookStatus(ids: number[] | number, status: string) {
    return gqlApiInstance.request(UPDATE_BOOKS_STATUS, {
      updateBookStatusInput: {
        ids,
        status,
      },
    })
  }

  async function updateBookIsEnded(id: number, isEnded: boolean) {
    return gqlApiInstance.request(UPDATE_BOOK_ISENDED, {
      updateBookInput: {
        id,
        isEnded,
      },
    })
  }

  async function removeBook(id: number) {
    return gqlApiInstance.request(REMOVE_BOOK, {
      removeBookId: id,
    })
  }

  async function removeBooks(ids: number[]) {
    return gqlApiInstance.request(REMOVE_BOOKS, {
      ids,
    })
  }

  async function getMyWritingOptions(
    page: number
  ): Promise<MyWritingOptionResponse> {
    const { getWritingList } = await gqlApiInstance.request(
      GET_WRITING_OPTIONS,
      {
        page,
        limitPerPage: 10,
      }
    )

    return plainToInstance(MyWritingOptionResponse, getWritingList)
  }

  async function getBooksTitleAndIdByUser(searchText: string) {
    const res = await gqlApiInstance.request(GET_BOOKS_TITLE_AND_ID_BY_USER, {
      searchText,
    })
    return plainToInstance(
      BookOptionType,
      res.getBooksTitleAndIdByUser as Array<BookOptionType>
    )
  }

  async function getWriterSalesAnalyticsOverview(
    params: SaleAndAnalyticsQueryParams
  ) {
    const res = await gqlApiInstance.request(
      GET_WRITER_SALES_ANALYTICS_OVERVIEW,
      {
        getWriterSalesAnalyticsOverviewInput: {
          ...params,
          categoryIds: params.categoryIds?.map(row => Number(row)),
          penNameIds: params.penNameIds?.map(row => Number(row)),
          startDate: params.startDate.toUTC(),
          endDate: params.endDate.toUTC(),
        },
      }
    )
    return plainToInstance(
      WriterSalesAnalyticsOverviewDataType,
      res.getWriterSalesAnalyticsOverview
    )
  }

  async function getWriterSalesAnalyticsDetail(
    params: SaleAndAnalyticsQueryParams,
    sortParams: SortTableType
  ) {
    const res = await gqlApiInstance.request(
      GET_WRITER_SALES_ANALYTICS_DETAIL,
      {
        getWriterSalesAnalyticsDetailInput: {
          ...params,
          bookId: [
            WriterSalesAnalyticsDetailReportEnum.SALES,
            WriterSalesAnalyticsDetailReportEnum.LIKE,
            WriterSalesAnalyticsDetailReportEnum.SHELF_ADDED,
          ].includes(sortParams.reportType)
            ? undefined
            : params.bookId,
          categoryIds: params.categoryIds?.map(row => Number(row)),
          penNameIds: params.penNameIds?.map(row => Number(row)),
          startDate: params.startDate.toUTC(),
          endDate: params.endDate.toUTC(),
          reportType: sortParams.reportType,
          orderBy: sortParams.sortKey
            ? { [sortParams.sortKey]: sortParams.sortValue }
            : undefined,
        },
      }
    )
    return plainToInstance(
      WriterSalesAnalyticsDetailDataType,
      res.getWriterSalesAnalyticsDetail
    )
  }

  async function getMyEbook(
    page: number,
    limitPerPage: number,
    searchParam: SearchFormType
  ): Promise<MyEbookResponse> {
    const { getEbookOverview } = await gqlApiInstance.request(GET_MY_EBOOK, {
      bookType: searchParam.bookType?.length ? searchParam.bookType : undefined,
      categoryIds: searchParam.category?.length
        ? searchParam.category.map(i => Number(i))
        : undefined,
      name: searchParam.param || '',
      penNameId: searchParam.penName?.length
        ? searchParam.penName.map(i => Number(i))
        : undefined,
      page,
      limitPerPage,
    })

    return plainToInstance(MyEbookResponse, getEbookOverview)
  }

  async function getEbooksTitleAndIdByUser(
    searchText: string
  ): Promise<EbookByUserIdType[]> {
    const res = await gqlApiInstance.request(GET_EBOOKS_BY_USER, {
      searchText,
    })
    return plainToInstance(
      EbookByUserIdType,
      res.getEbooksTitleAndIdByUser as []
    )
  }

  async function getWriterSalesAnalyticsEbookOverview(
    params: SaleAndAnalyticsQueryParams
  ): Promise<WriterSalesAnalyticsOverviewEbookDataType> {
    const res = await gqlApiInstance.request(
      GET_WRITER_SALES_ANALYTICS_EBOOK_OVERVIEW,
      {
        getWriterSalesAnalyticsEbookOverviewInput: {
          ebookId: params.ebookId,
          ebookTypes: params.bookTypes,
          categoryIds: params.categoryIds?.map(row => Number(row)),
          penNameIds: params.penNameIds?.map(row => Number(row)),
          startDate: params.startDate.toUTC(),
          endDate: params.endDate.toUTC(),
        },
      }
    )
    return plainToInstance(
      WriterSalesAnalyticsOverviewEbookDataType,
      res.getWriterSalesAnalyticsEbookOverview
    )
  }

  async function getWriterSalesAnalyticsEbookDetail(
    params: SaleAndAnalyticsQueryParams,
    sortParams: SortEbookTableType
  ) {
    const res = await gqlApiInstance.request(
      GET_WRITER_SALES_ANALYTICS_EBOOK_DETAIL,
      {
        getWriterSalesAnalyticsEbookDetailInput: {
          ebookId: params.ebookId,
          ebookTypes: params.bookTypes,
          categoryIds: params.categoryIds?.map(row => Number(row)),
          penNameIds: params.penNameIds?.map(row => Number(row)),
          startDate: params.startDate.toUTC(),
          endDate: params.endDate.toUTC(),
          reportType: sortParams.ebookReportType,
          orderBy: sortParams.sortKey
            ? { [sortParams.sortKey]: sortParams.sortValue }
            : undefined,
        },
      }
    )
    return plainToInstance(
      SalesAnalyticsDetailEbookDataType,
      res.getWriterSalesAnalyticsEbookDetail
    )
  }

  return {
    getMyWriting,
    updateBookStatus,
    updateBookIsEnded,
    removeBook,
    removeBooks,
    getMyWritingOptions,
    getBooksTitleAndIdByUser,
    getWriterSalesAnalyticsOverview,
    getWriterSalesAnalyticsDetail,
    getMyEbook,
    getEbooksTitleAndIdByUser,
    getWriterSalesAnalyticsEbookOverview,
    getWriterSalesAnalyticsEbookDetail,
  }
}
